import React, {Component} from "react";
import get from "lodash/get";
import compose from "lodash/flowRight";
import {graphql, withApollo} from "react-apollo";
import {withRouter} from "react-router-dom";
import RecordAnalytics from "../../../Analytics/RecordAnalytics";
import withAuth from "../../../Auth/withAuth/withAuth";
import withSendMessage from "../withSendMessage";
import withMessenger from "../withMessenger/withMessenger";
import withMessageListener from "../withMessageListener/withMessageListener";
import Messenger from "../../messenger";
import chatGroupAuxQuery from "../../queries/chatGroupAuxQuery";
import messagesQuery from "../../queries/messagesQuery";
import checkAllowDelinkedForApplicant from "../../utils/checkAllowDelinkedForApplicant";
import {isFeatureEnabled} from "../../../Utils/FeatureUtils";
import {getenv} from "../../../../util/getEnv";

export function withChatComponent(app = "jigglypuff") {
    const DEFAULT_PROFILE_PHOTO = getenv("DEFAULT_PROFILE_PHOTO");
    return function wrappedWithChatComponent(WrappedComponent) {
        class ChatComponent extends Component {
            state = {
                avatar: DEFAULT_PROFILE_PHOTO,
            };

            async componentDidMount() {
                const channel = await Messenger.subscribe(
                    this.props.match.params.chatGroupId,
                    this.props.connectionState,
                    this.props.pusher,
                );
                if (channel.subscribed) this.props.listenForMessages(channel);
            }

            async componentWillReceiveProps(newProps) {
                if (
                    get(newProps, "chatGroup", false) &&
                    get(newProps, "chatGroup") !== get(this.props, "chatGroup")
                ) {
                    const channel = await Messenger.subscribe(
                        newProps.chatGroup.id,
                        newProps.connectionState,
                        newProps.pusher,
                    );
                    if (channel.subscribed) this.props.listenForMessages(channel);
                }
            }

            onSendMessage = (text, metadata, timeSpent) => {
                if (text === "") return;
                // Legacy Event
                RecordAnalytics.event(this.props, "Send Chat Message", {
                    chatGroupId: get(this.props, "chatGroup.id"),
                    senderId: this.props.me.anyUser.id,
                });
                const universitySlug = get(this.props, "universitySlug", "");
                const meAuthState = get(this.props, "authState.me");
                const allowDelinked = checkAllowDelinkedForApplicant(universitySlug, meAuthState);

                const variables = {
                    text,
                    chatGroupId: this.props.match.params.chatGroupId,
                    skipNotifications: app === "horsea",
                    metadata,
                    timeSpent,
                    allowDelinked,
                };
                if (app === "horsea") {
                    variables.origin = {
                        type: "live-event",
                        slug: this.props.match.params.liveEventSlug,
                    };
                }
                // POSTs a SendMessageMutation via the function defined by withSendMessage.jsx
                return this.props.sendMessage({variables}, this.props.dispatch);
            };

            render() {
                const avatar =
                    get(this.props, "chatGroup.opponent.profilePhoto", null) ||
                    DEFAULT_PROFILE_PHOTO;
                return (
                    <WrappedComponent
                        onSendMessage={this.onSendMessage}
                        avatar={avatar}
                        {...this.props}
                    />
                );
            }
        }

        return ChatComponent;
    };
}

const withChat = (app = "jigglypuff") =>
    function withChatWrapped(WrappedComponent) {
        const messagesQueryOptions = {
            name: "messages",
            options(props) {
                // note: allowDelinked added check here as this is a common component shared bw diff apps, and allowDelinked should only be for applicant
                const universitySlug = get(props, "universitySlug", "");
                const meAuthState = get(props, "authState.me");
                const allowDelinked = checkAllowDelinkedForApplicant(universitySlug, meAuthState);
                const isDegreeTypesEnabled = isFeatureEnabled(
                    "enableDegreeTypes",
                    props.university,
                );

                return {
                    variables: {
                        chatGroupId: props.match.params.chatGroupId,
                        allowDelinked,
                        isDegreeTypesEnabled,
                    },
                    fetchPolicy: "cache-and-network",
                };
            },
            props({messages}) {
                return {
                    messages: get(messages, "messages.messages", []),
                    chatGroup: get(messages, "messages.chatGroup"),
                    loading: messages.loading,
                    messagesQueryError: messages.error,
                };
            },
        };

        const chatGroupAuxQueryOptions = {
            name: "chatGroupAux",
            options(props) {
                return {
                    variables: {
                        chatGroupId: props.match.params.chatGroupId,
                    },
                    fetchPolicy: "network-only",
                };
            },
            props({chatGroupAux}) {
                return {
                    chatGroupAux: chatGroupAux.chatGroupAux,
                    labels: chatGroupAux.chatGroupLabels,
                    loadingAux: chatGroupAux.loading,
                    refetchChatGroupAux: chatGroupAux.refetch,
                };
            },
        };
        const ChatComponent = withChatComponent(app)(WrappedComponent);
        return compose(
            withRouter,
            withApollo,
            withAuth,
            withSendMessage,
            withMessenger,
            withMessageListener,
            graphql(messagesQuery, messagesQueryOptions),
            graphql(chatGroupAuxQuery, chatGroupAuxQueryOptions),
        )(ChatComponent);
    };

export default withChat;
